import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useAccessApi, ProfileUpdateRequest, ProfileUpdateError } from '../services/api';
import { Button } from '../components/ui/button';
import {
    buildProfileUpdateRequest,
    extractRestrictedFieldsFromProfile,
    mergeRestrictedFieldsFromErrorMessage,
} from './ProfilePage.helpers';
import { ProfileEditForm, ProfileReadOnlyView } from './ProfilePageSections';

export function ProfilePage() {
    const { getAuth0Profile, updateAuth0Profile } = useAccessApi();
    const queryClient = useQueryClient();
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState<ProfileUpdateRequest>({});
    const [restrictedFields, setRestrictedFields] = useState<string[]>([]);

    // Query for user profile
    const {
        data: profileData,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['auth0Profile'],
        queryFn: getAuth0Profile,
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 2,
    });

    // Mutation for updating profile
    const updateProfileMutation = useMutation({
        mutationFn: updateAuth0Profile,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['auth0Profile'] });
            setIsEditing(false);
            setFormData({});
        },
        onError: (error) => {
            console.error('Profile update failed:', error);
            const errorMessage =
                error instanceof Error ? error.message : (error as ProfileUpdateError).error || '';
            setRestrictedFields((prev) =>
                mergeRestrictedFieldsFromErrorMessage(prev, errorMessage),
            );
        },
    });

    const profile = profileData?.profile;

    useEffect(() => {
        if (!profile) {
            return;
        }

        setRestrictedFields((prev) =>
            prev.length > 0 ? prev : extractRestrictedFieldsFromProfile(profile),
        );
    }, [profile]);

    const handleEdit = () => {
        if (profile) {
            setFormData({
                given_name: profile.given_name || '',
                family_name: profile.family_name || '',
                name: profile.name || '',
                email: profile.email || '',
                picture: profile.picture || '',
                user_metadata: profile.user_metadata || {},
            });
        }
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setFormData({});
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const updates = buildProfileUpdateRequest({
            formData,
            profile,
            restrictedFields,
        });

        // Only update if we have changes
        if (Object.keys(updates).length > 0) {
            updateProfileMutation.mutate(updates);
        } else {
            setIsEditing(false);
        }
    };

    const handleInputChange = (field: keyof ProfileUpdateRequest, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-64">
                <LoadingSpinner />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="text-center text-red-600">
                    <h3 className="text-lg font-semibold mb-2">Error Loading Profile</h3>
                    <p>{error instanceof Error ? error.message : 'Unknown error occurred'}</p>
                </div>
            </div>
        );
    }

    if (!profile) {
        return (
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="text-center text-neutral-600">Profile not found</div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="bg-white rounded-lg shadow-md">
                {/* Header */}
                <div className="border-b border-neutral-200 px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-neutral-900">Profile</h1>
                            <p className="text-sm text-neutral-600 mt-1">
                                Manage your personal information and account settings
                            </p>
                        </div>
                        {!isEditing && (
                            <Button onClick={handleEdit} variant="outline">
                                Edit Profile
                            </Button>
                        )}
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {isEditing ? (
                        <ProfileEditForm
                            formData={formData}
                            restrictedFields={restrictedFields}
                            isPending={updateProfileMutation.isPending}
                            error={
                                updateProfileMutation.error instanceof Error
                                    ? updateProfileMutation.error
                                    : null
                            }
                            onInputChange={handleInputChange}
                            onSubmit={handleSubmit}
                            onCancel={handleCancel}
                        />
                    ) : (
                        <ProfileReadOnlyView
                            profile={profile}
                            restrictedFields={restrictedFields}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
